// src/components/work/WorkSummaryCards.jsx
// Smart + Dumb in one file
// -----------------------------------------------------------------------------
// This file now contains:
//   1) SmartWorkSummaryCards (DEFAULT EXPORT)  → fetches + computes KPIs inline
//   2) WorkSummaryCardsView (presentational)   → renders the cards
//
// Minimal usage (same import as before):
//   <WorkSummaryCards onOpenDetails={openDetails} onShowUnscheduled={...} />
//
// Optional tuning:
//   capacityHoursPerTechDay={8}
//   defaultDurations={{ task: 60, job: 90, appointment: 60 }}
//   compact   ← tighter header UI (or density="compact" / size="sm")
// -----------------------------------------------------------------------------

import React, { memo, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';

// SMART bits (data + filters)
import useWorkFilters from '../../hooks/useWorkFilters';
import { useWorkByRange } from '../../hooks/useCalendarQueries';
import { useWorkList } from '../../hooks/useWorkQueries';

/* ────────────────────────────── tiny utils ────────────────────────────── */

const cx = (...xs) => xs.filter(Boolean).join(' ');
const nf = (n) => new Intl.NumberFormat().format(Math.max(0, Number.isFinite(n) ? n : 0));
const pf = (v) => `${Math.max(0, Math.min(100, Math.round(v)))}%`;

const MIN_SLOT_MINUTES = 30;
const WORKDAY_START_HOUR = 9; // 9am local
const WEEKDAYS = new Set([1, 2, 3, 4, 5]); // Mon..Fri

const toDate = (v) => {
  const d = v instanceof Date ? v : new Date(v);
  return Number.isFinite(d?.getTime?.()) ? d : null;
};

const parseYMD = (s) => {
  if (!s) return null;
  const [y, m, d] = String(s).split('-').map(Number);
  const dt = new Date(y, (m || 1) - 1, d || 1, 0, 0, 0, 0);
  return Number.isFinite(dt.getTime()) ? dt : null;
};

const fmtYMD = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
const addMinutes = (d, mins) => new Date(d.getTime() + mins * 60_000);
const addDays = (d, days) =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate() + days, d.getHours(), d.getMinutes(), 0, 0);

const clampToDayWindow = (d, hoursPerDay) => {
  const start = new Date(d.getFullYear(), d.getMonth(), d.getDate(), WORKDAY_START_HOUR, 0, 0, 0);
  const end = new Date(d.getFullYear(), d.getMonth(), d.getDate(), WORKDAY_START_HOUR + hoursPerDay, 0, 0, 0);
  if (d < start) return start;
  if (d > end) return end;
  return d;
};

const dayWindow = (d, hoursPerDay) => ({
  start: new Date(d.getFullYear(), d.getMonth(), d.getDate(), WORKDAY_START_HOUR, 0, 0, 0),
  end: new Date(d.getFullYear(), d.getMonth(), d.getDate(), WORKDAY_START_HOUR + hoursPerDay, 0, 0, 0),
});

const isWeekday = (d) => WEEKDAYS.has(d.getDay());

const iterDays = (from, to, fn) => {
  let cur = new Date(from.getFullYear(), from.getMonth(), from.getDate(), 0, 0, 0, 0);
  const end = new Date(to.getFullYear(), to.getMonth(), to.getDate(), 0, 0, 0, 0);
  while (cur <= end) {
    fn(cur);
    cur = addDays(cur, 1);
  }
};

const getAssigneeIds = (w) => {
  const arr = w?.assignees || w?.assigneeIds || [];
  return arr
    .map((a) => (typeof a === 'string' ? a : a?._id || a?.id || a?.value))
    .filter(Boolean);
};

const getType = (w) => (w?.type || '').toLowerCase() || 'task';

const defaultDurationsMins = { task: 60, job: 90, appointment: 60 };

const getDefaultMinutesFor = (w, map) => {
  const t = getType(w);
  const m = map?.[t];
  return Number.isFinite(m) ? m : (defaultDurationsMins[t] || 60);
};

// ── TIME SHAPE NORMALIZATION (supports schedule.* and top-level start/end) ──
const getStart = (w) => toDate(w?.schedule?.start ?? w?.start);
const getEnd = (w) => toDate(w?.schedule?.end ?? w?.end);
const getAllDay = (w) => Boolean(w?.schedule?.allDay ?? w?.allDay);

const durationHours = (w, defaultMap, hoursPerDay) => {
  const s = getStart(w);
  const e = getEnd(w);
  const allDay = getAllDay(w);
  if (s && e && e > s && !allDay) {
    const h = (e - s) / 3_600_000;
    return Math.max(0.25, h); // floor at 15m
  }
  if (allDay) return Math.max(1, hoursPerDay);
  return Math.max(0.5, (getDefaultMinutesFor(w, defaultMap) || 60) / 60);
};

const countDistinctAssignees = (items) => {
  const set = new Set();
  items.forEach((w) => getAssigneeIds(w).forEach((id) => set.add(String(id))));
  return set.size;
};

const workDaysInRange = (from, to) => {
  let n = 0;
  iterDays(from, to, (d) => {
    if (isWeekday(d)) n += 1;
  });
  return n;
};

const estimateScheduledPersonHours = (items, defaultMap, hoursPerDay) =>
  items.reduce((sum, w) => sum + durationHours(w, defaultMap, hoursPerDay) * Math.max(1, getAssigneeIds(w).length), 0);

const findNextJob = (items, now) => {
  let best = null;
  items.forEach((w) => {
    const s = getStart(w);
    if (s && s > now) {
      if (!best || s < getStart(best)) best = w;
    }
  });
  return best;
};

const estimateBacklogHours = (unscheduled, defaultMap) =>
  unscheduled.reduce((sum, w) => sum + (getDefaultMinutesFor(w, defaultMap) / 60), 0);

const detectConflicts = (items) => {
  const byAssignee = new Map();
  items.forEach((w) => {
    const s = getStart(w);
    const e = getEnd(w);
    if (!s || !e || !(e > s)) return;
    const ids = getAssigneeIds(w);
    if (!ids.length) return;
    ids.forEach((id) => {
      const arr = byAssignee.get(id) || [];
      arr.push([s, e]);
      byAssignee.set(id, arr);
    });
  });
  let conflicts = 0;
  byAssignee.forEach((arr) => {
    arr.sort((a, b) => a[0] - b[0]);
    for (let i = 1; i < arr.length; i++) {
      if (arr[i][0] < arr[i - 1][1]) conflicts += 1;
    }
  });
  return conflicts;
};

const isBusyDuring = (s, e, t0, t1) => s < t1 && e > t0;

// Naive: first 30m slot where not all techs are busy
const findFirstAvailableSlot = ({ items, from, to, hoursPerDay, slotMinutes = MIN_SLOT_MINUTES }) => {
  if (!from || !to) return null;
  const now = new Date();
  const distinctTechs = countDistinctAssignees(items);
  if (distinctTechs === 0) return null;

  let found = null;

  iterDays(from, to, (day) => {
    if (found || !isWeekday(day)) return;

    const window = dayWindow(day, hoursPerDay);
    let scanStart = window.start;
    if (now.toDateString() === day.toDateString()) {
      scanStart = clampToDayWindow(now, hoursPerDay);
    }
    for (let t = scanStart; t < window.end; t = addMinutes(t, slotMinutes)) {
      const t1 = addMinutes(t, slotMinutes);
      const seen = new Set();
      items.forEach((w) => {
        const s = getStart(w);
        const e = getEnd(w);
        if (!s || !e) return;
        if (!isBusyDuring(s, e, t, t1)) return;
        getAssigneeIds(w).forEach((id) => seen.add(String(id)));
      });
      const busyTechs = seen.size;
      if (busyTechs < distinctTechs) {
        found = new Date(t);
        return;
      }
    }
  });

  return found;
};

/* ────────────────────────── UI primitives ────────────────────────── */

const Card = ({ children, onClick, title, className, role = 'button', compact }) => (
  <button
    type="button"
    role={role}
    aria-label={title}
    title={title}
    onClick={onClick}
    className={cx(
      'group relative w-full rounded-2xl border border-slate-200 bg-white shadow-sm transition',
      'hover:shadow-md focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-indigo-500/30',
      className
    )}
  >
    <div className={cx(compact ? 'p-3 sm:p-4' : 'p-4 sm:p-5')}>{children}</div>
    <div className="pointer-events-none absolute inset-0 rounded-2xl ring-1 ring-inset ring-black/5" />
  </button>
);

const Label = ({ children, compact }) => (
  <div className={cx(compact ? 'text-[11px]' : 'text-[12px]', 'font-medium uppercase tracking-wide text-slate-600')}>
    {children}
  </div>
);

const Big = ({ children, compact }) => (
  <div
    className={cx(
      'font-semibold leading-none text-slate-900',
      compact ? 'mt-0.5 text-[22px] sm:text-[24px]' : 'mt-1 text-[28px]'
    )}
  >
    {children}
  </div>
);

const Small = ({ children, compact }) => (
  <div className={cx(compact ? 'mt-0.5 text-[12px]' : 'mt-1 text-[13px]', 'text-slate-500')}>{children}</div>
);

const Dot = ({ className = 'bg-amber-500' }) => <span className={cx('inline-block h-2 w-2 rounded-full', className)} />;

const Bar = ({ pct }) => (
  <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-slate-100">
    <div
      className={cx(
        'h-full rounded-full transition-all',
        pct >= 100 ? 'bg-rose-500' : pct >= 85 ? 'bg-amber-500' : 'bg-emerald-500'
      )}
      style={{ width: pf(pct) }}
    />
  </div>
);

/* ────────────────────────── Presentational View ────────────────────────── */

export const WorkSummaryCardsView = memo(function WorkSummaryCardsView({
  // KPI inputs / data
  itemsInRange = [],
  unscheduledItems = [],
  range = { from: '', to: '' },
  capacityHoursPerTechDay = 8,
  defaultDurations = defaultDurationsMins,

  // Next job override (from look-ahead search)
  nextJobOverride = null,

  // Existing props (compat)
  counts = {},
  activeType,
  activeHasConflicts = false,
  loading = false,
  compact = false,
  density,
  size,
  className,
  onFilterToggle,
  onShowAll,

  // Actions
  onOpenDetails,
  onShowUnscheduled,
  onToggleConflicts,
}) {
  const isCompact = compact || density === 'compact' || size === 'sm';

  const from = parseYMD(range?.from);
  const to = parseYMD(range?.to);
  const now = new Date();

  // Fallback counts from current items if server totals not provided
  const typeCounts = useMemo(() => {
    const base = { task: 0, job: 0, appointment: 0 };
    itemsInRange.forEach((w) => {
      const t = getType(w);
      if (base[t] != null) base[t] += 1;
    });
    return base;
  }, [itemsInRange]);

  const safeCounts = {
    tasks: Number.isFinite(counts.tasks) ? counts.tasks : typeCounts.task,
    jobs: Number.isFinite(counts.jobs) ? counts.jobs : typeCounts.job,
    appointments: Number.isFinite(counts.appointments) ? counts.appointments : typeCounts.appointment,
    conflicts: Number.isFinite(counts.conflicts) ? counts.conflicts : undefined,
  };

  // KPIs (estimated)
  const kpis = useMemo(() => {
    if (!from || !to) {
      return {
        utilizationPct: 0,
        scheduledHours: 0,
        capacityHours: 0,
        nextJob: nextJobOverride || null,
        backlogCount: unscheduledItems?.length || 0,
        backlogHours: estimateBacklogHours(unscheduledItems || [], defaultDurations),
        conflicts: 0,
        firstAvailable: null,
        isEstimated: true,
      };
    }

    const workingDays = workDaysInRange(from, to);
    const techs = countDistinctAssignees(itemsInRange);
    const capacityHours = techs * workingDays * capacityHoursPerTechDay;
    const scheduledHours = estimateScheduledPersonHours(itemsInRange, defaultDurations, capacityHoursPerTechDay);
    const utilizationPct = capacityHours > 0 ? (scheduledHours / capacityHours) * 100 : 0;

    const nextJob = nextJobOverride || findNextJob(itemsInRange, now);
    const backlogCount = (unscheduledItems || []).length;
    const backlogHours = estimateBacklogHours(unscheduledItems || [], defaultDurations);

    const conflicts = Number.isFinite(safeCounts.conflicts) ? safeCounts.conflicts : detectConflicts(itemsInRange);

    // First available: clamp starting day to today (never search the past)
    const fromClamped =
      from > now ? from : new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
    const firstAvailable = findFirstAvailableSlot({
      items: itemsInRange,
      from: fromClamped,
      to,
      hoursPerDay: capacityHoursPerTechDay,
      slotMinutes: MIN_SLOT_MINUTES,
    });

    return {
      utilizationPct,
      scheduledHours,
      capacityHours,
      nextJob,
      backlogCount,
      backlogHours,
      conflicts,
      firstAvailable,
      isEstimated: true,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    itemsInRange,
    unscheduledItems,
    range?.from,
    range?.to,
    capacityHoursPerTechDay,
    defaultDurations,
    safeCounts.conflicts,
    nextJobOverride,
  ]);

  const handleShowAll = useCallback(() => {
    if (typeof onShowAll === 'function') onShowAll();
    if (typeof onFilterToggle === 'function') onFilterToggle({ type: undefined, hasConflicts: undefined });
  }, [onShowAll, onFilterToggle]);

  const grid = 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-5';

  const nextJobTime = useMemo(() => {
    if (!kpis.nextJob) return null;
    const s = getStart(kpis.nextJob);
    const e = getEnd(kpis.nextJob);
    if (!s) return null;
    const timeFmt = new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' });
    const label = e ? `${timeFmt.format(s)} – ${timeFmt.format(e)}` : timeFmt.format(s);
    return label;
  }, [kpis.nextJob]);

  const firstAvailLabel = useMemo(() => {
    if (!kpis.firstAvailable) return 'n/a';
    const fmt = new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' });
    return fmt.format(kpis.firstAvailable);
  }, [kpis.firstAvailable]);

  return (
    <section
      aria-label="Work Summary"
      className={cx(isCompact ? 'space-y-2' : 'space-y-3', className)}
    >
      <div className={cx('grid', isCompact ? 'gap-2' : 'gap-3', grid)}>
        {/* ALL (Reset) */}
        <Card onClick={handleShowAll} title="Show all" compact={isCompact}>
          <Label compact={isCompact}>All</Label>
          <Big compact={isCompact}>
            {nf(safeCounts.tasks + safeCounts.jobs + safeCounts.appointments)}
          </Big>
          <Small compact={isCompact}>Everything in the current window</Small>
        </Card>

        {/* Crew Utilization */}
        <Card onClick={() => {}} title="Crew Utilization (estimated)" compact={isCompact}>
          <div className="flex items-center justify-between">
            <Label compact={isCompact}>Crew Utilization</Label>
            <span
              className={cx('inline-flex items-center gap-1 text-slate-500', isCompact ? 'text-[10px]' : 'text-[11px]')}
              title="Estimated based on 8h/tech/day"
            >
              <Dot className="bg-amber-500" /> estimated
            </span>
          </div>
          <Big compact={isCompact}>{pf(kpis.utilizationPct)}</Big>
          <Bar pct={kpis.utilizationPct} />
          <Small compact={isCompact}>
            {nf(Math.round(kpis.scheduledHours))}h / {nf(Math.round(kpis.capacityHours))}h window
          </Small>
        </Card>

        {/* Next Job */}
        <Card
          onClick={() => {
            if (!kpis.nextJob || typeof onOpenDetails !== 'function') return;
            const workId =
              kpis.nextJob?.work?._id ||
              kpis.nextJob?.workId ||
              kpis.nextJob?._id ||
              kpis.nextJob?.id;
            if (workId) onOpenDetails(workId);
          }}
          title="Next job"
          compact={isCompact}
          className={cx(!kpis.nextJob && 'opacity-60 cursor-not-allowed')}
        >
          <Label compact={isCompact}>Next Job</Label>
          <Big compact={isCompact}>{kpis.nextJob?.title || kpis.nextJob?.name || '—'}</Big>
          <Small compact={isCompact}>{nextJobTime || 'None scheduled'}</Small>
        </Card>

        {/* Conflicts */}
        <Card
          onClick={() => typeof onToggleConflicts === 'function' && onToggleConflicts(true)}
          title="Conflicts"
          compact={isCompact}
        >
          <Label compact={isCompact}>Conflicts</Label>
          <Big compact={isCompact}>{nf(kpis.conflicts || 0)}</Big>
          <Small compact={isCompact}>
            {kpis.conflicts ? 'Click to review overlaps' : 'No overlaps detected'}
          </Small>
        </Card>

        {/* First Available Crew */}
        <Card onClick={() => {}} title="First available crew" compact={isCompact}>
          <Label compact={isCompact}>First Available Crew</Label>
          <Big compact={isCompact}>{firstAvailLabel}</Big>
          <Small compact={isCompact}>Naive 30-min gap within working window</Small>
        </Card>
      </div>
    </section>
  );
});

WorkSummaryCardsView.propTypes = {
  itemsInRange: PropTypes.array,
  unscheduledItems: PropTypes.array,
  range: PropTypes.shape({ from: PropTypes.string, to: PropTypes.string }),
  capacityHoursPerTechDay: PropTypes.number,
  defaultDurations: PropTypes.object,
  nextJobOverride: PropTypes.object,
  counts: PropTypes.shape({
    tasks: PropTypes.number,
    jobs: PropTypes.number,
    appointments: PropTypes.number,
    conflicts: PropTypes.number,
  }),
  activeType: PropTypes.oneOf(['task', 'job', 'appointment']),
  activeHasConflicts: PropTypes.bool,
  loading: PropTypes.bool,
  compact: PropTypes.bool,
  density: PropTypes.string,
  size: PropTypes.string,
  className: PropTypes.string,
  onFilterToggle: PropTypes.func,
  onShowAll: PropTypes.func,
  onOpenDetails: PropTypes.func,
  onShowUnscheduled: PropTypes.func,
  onToggleConflicts: PropTypes.func,
};

/* ───────────────────────────── Smart Wrapper ───────────────────────────── */

function SmartWorkSummaryCards({
  // Optional overrides / tuning
  capacityHoursPerTechDay = 8,
  defaultDurations = defaultDurationsMins,

  // Optional data overrides
  itemsInRange,
  unscheduledItems,
  range,
  counts,

  // Actions
  onOpenDetails,
  onShowUnscheduled,
  onToggleConflicts,

  // Legacy/compat props (accepted; UI density handled in view)
  activeType,
  activeHasConflicts,
  loading,
  compact,
  density,
  size,
  className,
  onFilterToggle,
  onShowAll,
}) {
  const { filters, setFilters } = useWorkFilters();

  // 🔒 Do NOT mutate global filters here.
  // Instead, compute a local fallback current-month range for the header KPIs only.
  const fallbackRange = useMemo(() => {
    // If caller passed a range OR global filters already have from/to, no fallback needed.
    if ((range?.from && range?.to) || (filters.from && filters.to)) return null;
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return { from: fmtYMD(start), to: fmtYMD(end) };
  }, [range?.from, range?.to, filters.from, filters.to]);

  // Effective window the header uses for its own queries
  const activeFrom = range?.from || filters.from || fallbackRange?.from || '';
  const activeTo = range?.to || filters.to || fallbackRange?.to || '';

  // Build parameters for range query
  const rangeParams = useMemo(() => {
    if (!activeFrom || !activeTo) return null;
    return {
      from: activeFrom,
      to: activeTo,
      q: undefined, // summary stays unbiased by free text
      type: filters.type || undefined,
      status: filters.status || undefined,
      priority: filters.priority || undefined,
      assigneeId: filters.assigneeId || undefined,
      clientId: filters.clientId || undefined,
      assigneeName: filters.assigneeName || undefined,
      clientName: filters.clientName || undefined,
      includeCancelled: !!filters.includeCancelled,
    };
  }, [activeFrom, activeTo, filters]);

  // In-window items (for utilization / conflicts / first-available)
  const inRangeQ = useWorkByRange(rangeParams || {}, {
    enabled: Boolean(rangeParams?.from && rangeParams?.to),
    refetchOnWindowFocus: false,
  });

  // Look-ahead (today → +45 days) for “Next Job”
  const today = useMemo(() => fmtYMD(new Date()), []);
  const lookAheadTo = useMemo(() => fmtYMD(addDays(new Date(), 45)), []);
  const lookAheadParams = useMemo(
    () => ({
      from: today,
      to: lookAheadTo,
      q: undefined,
      type: filters.type || undefined,
      status: filters.status || undefined,
      priority: filters.priority || undefined,
      assigneeId: filters.assigneeId || undefined,
      clientId: filters.clientId || undefined,
      assigneeName: filters.assigneeName || undefined,
      clientName: filters.clientName || undefined,
      includeCancelled: !!filters.includeCancelled,
    }),
    [today, lookAheadTo, filters]
  );

  const lookAheadQ = useWorkByRange(lookAheadParams, {
    enabled: true,
    refetchOnWindowFocus: false,
  });

  // Unscheduled backlog (always fetch, capped)
  const unscheduledParams = {
    ...filters,
    from: undefined,
    to: undefined,
    page: 1,
    limit: 200,
    sort: 'createdAt:desc',
    onlyUnscheduled: 1,
  };
  const unscheduledQ = useWorkList(unscheduledParams);
  const unscheduledAll = (unscheduledQ.items || []).filter((it) => !getStart(it));

  // Server totals (outside date window) for the All/Tasks/Jobs/Appointments cards
  const baseCountFilters = useMemo(() => ({ ...filters, from: undefined, to: undefined }), [filters]);
  const tasksQ = useWorkList({ ...baseCountFilters, type: ['task'], page: 1, limit: 0 });
  const jobsQ = useWorkList({ ...baseCountFilters, type: ['job'], page: 1, limit: 0 });
  const apptsQ = useWorkList({ ...baseCountFilters, type: ['appointment'], page: 1, limit: 0 });

  const totals = useMemo(() => {
    const totalOf = (q) =>
      (typeof q?.meta?.total === 'number'
        ? q.meta.total
        : typeof q?.meta?.pagination?.total === 'number'
        ? q.meta.pagination.total
        : Array.isArray(q?.items)
        ? q.items.length
        : 0);

    return {
      tasks: totalOf(tasksQ),
      jobs: totalOf(jobsQ),
      appointments: totalOf(apptsQ),
    };
  }, [tasksQ, jobsQ, apptsQ]);

  // Conflict count (prefer server totals if provided on range API)
  const conflictCount =
    inRangeQ?.totals?.conflicts ??
    ((inRangeQ?.items || []).length ? detectConflicts(inRangeQ.items) : 0);

  // Merge external overrides (if caller passes data explicitly)
  const mergedItemsInRange = Array.isArray(itemsInRange) ? itemsInRange : (inRangeQ.items || []);
  const mergedUnscheduled = Array.isArray(unscheduledItems) ? unscheduledItems : unscheduledAll;
  const mergedRange = range || { from: activeFrom || '', to: activeTo || '' };
  const mergedCounts = {
    tasks: counts?.tasks ?? totals.tasks,
    jobs: counts?.jobs ?? totals.jobs,
    appointments: counts?.appointments ?? totals.appointments,
    conflicts: counts?.conflicts ?? conflictCount,
  };

  // Compute next job from look-ahead items (earliest > now)
  const nextJob = useMemo(() => {
    const items = lookAheadQ.items || [];
    const now = new Date();
    return findNextJob(items, now);
  }, [lookAheadQ.items]);

  // Wire simple filter toggles so the header is self-contained
  const handleToggle = useCallback(
    (partial) => {
      if (!partial) return;
      if (Object.prototype.hasOwnProperty.call(partial, 'type')) {
        const t = partial.type;
        setFilters({ type: t ? [t] : null });
      }
      if (Object.prototype.hasOwnProperty.call(partial, 'hasConflicts')) {
        setFilters({ hasConflicts: partial.hasConflicts ? true : undefined });
      }
    },
    [setFilters]
  );

  const handleShowAll = useCallback(() => {
    setFilters({ type: null, hasConflicts: undefined });
  }, [setFilters]);

  // Render presentational view with merged data
  return (
    <WorkSummaryCardsView
      itemsInRange={mergedItemsInRange}
      unscheduledItems={mergedUnscheduled}
      range={mergedRange}
      capacityHoursPerTechDay={capacityHoursPerTechDay}
      defaultDurations={defaultDurations}
      nextJobOverride={nextJob}
      counts={mergedCounts}
      // pass through for compatibility
      activeType={activeType}
      activeHasConflicts={activeHasConflicts}
      loading={loading || inRangeQ.isLoading}
      compact={compact}
      density={density}
      size={size}
      className={className}
      onFilterToggle={handleToggle}
      onShowAll={handleShowAll}
      onOpenDetails={onOpenDetails}
      onShowUnscheduled={onShowUnscheduled}
      onToggleConflicts={(enabled) => setFilters({ hasConflicts: enabled ? true : undefined })}
    />
  );
}

SmartWorkSummaryCards.propTypes = {
  capacityHoursPerTechDay: PropTypes.number,
  defaultDurations: PropTypes.object,
  itemsInRange: PropTypes.array,
  unscheduledItems: PropTypes.array,
  range: PropTypes.shape({ from: PropTypes.string, to: PropTypes.string }),
  counts: PropTypes.object,
  onOpenDetails: PropTypes.func,
  onShowUnscheduled: PropTypes.func,
  onToggleConflicts: PropTypes.func,
  // legacy/compat:
  activeType: PropTypes.oneOf(['task', 'job', 'appointment']),
  activeHasConflicts: PropTypes.bool,
  loading: PropTypes.bool,
  compact: PropTypes.bool,
  density: PropTypes.string,
  size: PropTypes.string,
  className: PropTypes.string,
  onFilterToggle: PropTypes.func,
  onShowAll: PropTypes.func,
};

export default SmartWorkSummaryCards;
